import { useState } from "react";
import { Building2, Pencil, RotateCcw } from "lucide-react";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useSelectableOffices } from "../hooks";
import type { StampDutyOfficeInfo } from "../types";

const SOURCE_HINTS: Record<StampDutyOfficeInfo["source"], string> = {
  manual: "wskazany ręcznie",
  config: "z konfiguracji mocodawcy (rejestr pełnomocnictw)",
  city: "rozpoznany z miasta siedziby wnioskodawcy (pole 1 wniosku)",
  recipient: "urząd-adresat wniosku (przy MPZP pełnomocnictwo składamy tam, gdzie wniosek)",
  none: "nie rozpoznano",
};

/** Urząd opłaty skarbowej 17 zł — pokazuje, KOGO system uznał za mocodawcę i skąd wziął
 *  urząd, oraz pozwala ten wybór poprawić. Opłata idzie do urzędu siedziby PODMIOTU
 *  WNIOSKUJĄCEGO (pole 1 wniosku), nie do adresata z pola 3 — dlatego obok nazwy urzędu
 *  stoi zawsze mocodawca i miasto jego siedziby.
 *
 *  Kontrolka jest sterowana: `value` to id wskazanego ręcznie urzędu ("" = rozpoznanie
 *  automatyczne). Modal wysyła je razem ze swoim payloadem, a `onSaveOnly` (jeśli podane)
 *  zapisuje sam urząd, bez zakładania opłaty. `lead` nadpisuje pierwsze zdanie — MPZP ma
 *  własną regułę (pełnomocnictwo składamy w urzędzie-adresacie). */
export function StampDutyOfficePicker({
  info, value, onChange, onSaveOnly, disabled, lead,
}: {
  info: StampDutyOfficeInfo;
  value: string;
  onChange: (officeId: string) => void;
  onSaveOnly?: () => void;
  disabled?: boolean;
  lead?: string;
}) {
  const unresolved = !info.office_name && !value;
  const [open, setOpen] = useState(unresolved);
  const { data: offices } = useSelectableOffices(value);
  const picked = (offices ?? []).find((o) => o.id === value);
  const shownName = picked?.name || info.office_name;

  return (
    <div className="rounded border border-[var(--color-border)] px-2.5 py-2 space-y-2">
      <div className="flex items-start gap-2">
        <Building2 size={14} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
        <div className="flex-1">
          <div className="text-[var(--color-text-secondary)]">
            {lead ?? "Opłata idzie do urzędu siedziby podmiotu wnioskującego (pole 1 wniosku), nie do adresata wniosku:"}{" "}
            <b className={shownName ? "" : "text-amber-700 dark:text-amber-400"}>
              {shownName || "— nie rozpoznano, wskaż urząd"}
            </b>
          </div>
          <div className="mt-0.5 text-[var(--color-text-muted)]">
            Mocodawca: {info.principal}
            {info.seat_city ? ` · siedziba: ${info.seat_city}` : ""}
            {" · "}
            {value ? SOURCE_HINTS.manual : SOURCE_HINTS[info.source]}
          </div>
        </div>
        {!open && (
          <button
            type="button" className="shrink-0 inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            onClick={() => setOpen(true)} disabled={disabled}
          >
            <Pencil size={11} /> Zmień urząd
          </button>
        )}
      </div>
      {open && (
        <div className="space-y-1.5">
          <SearchableSelect
            options={(offices ?? []).map((o) => ({
              value: o.id, label: o.city ? `${o.name} — ${o.city}` : o.name,
            }))}
            value={value}
            placeholder="Wybierz urząd opłaty skarbowej…"
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex items-center gap-3">
            {(value || info.manual) && (
              <button
                type="button" className="inline-flex items-center gap-1 text-[var(--color-text-muted)] hover:underline"
                onClick={() => onChange("")} disabled={disabled}
              >
                <RotateCcw size={11} /> Wróć do rozpoznania automatycznego
              </button>
            )}
            {onSaveOnly && (
              <button
                type="button" className="text-[var(--color-primary)] hover:underline"
                onClick={onSaveOnly} disabled={disabled}
              >
                Zapisz sam urząd
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
